import { useCallback, useEffect, useRef, useState } from "react";
import { Badge, Tooltip } from "@mantine/core";
import { IconPhoto } from "@tabler/icons-react";
import { type GroupImage } from "@labeling-tools/out-of-stock/src/utils/groupedImages";

const THUMB_WIDTH = 96;
const THUMB_HEIGHT = 64;

interface ImageFilmstripProps {
  images: GroupImage[];
  imageUrls: Map<string, string>;
  activeImageId: string;
  onSelect: (imageId: string) => void;
  boxCounts?: Map<string, number>;
}

/** Renders nothing for an ungrouped task, so callers can mount it always. */
export const ImageFilmstrip = ({
  images,
  imageUrls,
  activeImageId,
  onSelect,
  boxCounts,
}: ImageFilmstripProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [overflow, setOverflow] = useState({ left: false, right: false });

  const syncOverflow = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setOverflow({ left: el.scrollLeft > 1, right: el.scrollLeft < max - 1 });
  }, []);

  const activeIndex = images.findIndex(
    (image) => image.imageId === activeImageId,
  );
  const activeViewNumber = images[activeIndex]?.viewNumber;

  useEffect(() => {
    if (activeViewNumber == null) return;
    const tile = scrollRef.current?.querySelector(
      `[data-testid="filmstrip-view-${activeViewNumber}"]`,
    );
    tile?.scrollIntoView?.({
      block: "nearest",
      inline: "center",
      behavior: "smooth",
    });
  }, [activeViewNumber]);

  useEffect(() => {
    syncOverflow();
  }, [images, syncOverflow]);

  useEffect(() => {
    if (images.length <= 1) return;
    const onKeyDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      if (activeIndex === -1) return;
      const next = activeIndex + (e.key === "ArrowLeft" ? -1 : 1);
      if (next < 0 || next >= images.length) return;
      e.preventDefault();
      onSelect(images[next].imageId);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [images, activeIndex, onSelect]);

  if (images.length <= 1) return null;

  return (
    <div className="relative bg-gray-50 border-t border-gray-200">
      {overflow.left && (
        <div className="pointer-events-none absolute inset-y-0 left-0 w-12 z-10 bg-gradient-to-r from-gray-50 to-transparent" />
      )}
      {overflow.right && (
        <div className="pointer-events-none absolute inset-y-0 right-0 w-12 z-10 bg-gradient-to-l from-gray-50 to-transparent" />
      )}
      <div
        ref={scrollRef}
        onScroll={syncOverflow}
        className="flex items-center gap-2 px-3 py-2 overflow-x-auto"
      >
        {images.map((image) => {
          const isActive = image.imageId === activeImageId;
          const url = imageUrls.get(image.imageId);
          const boxCount = boxCounts?.get(image.imageId) ?? 0;
          const label = image.isPrimary
            ? "Primary view"
            : `Similar view ${image.viewNumber}`;

          const tooltip = [
            label,
            image.capturedAtUtc
              ? `Captured ${new Date(image.capturedAtUtc).toLocaleString()}`
              : null,
            boxCount === 1 ? "1 empty area" : `${boxCount} empty areas`,
          ]
            .filter(Boolean)
            .join(" · ");

          return (
            <Tooltip key={image.imageId} label={tooltip} withArrow>
              <button
                type="button"
                onClick={() => onSelect(image.imageId)}
                aria-label={label}
                aria-pressed={isActive}
                data-testid={`filmstrip-view-${image.viewNumber}`}
                className={`relative flex-shrink-0 rounded-md overflow-hidden border-2 transition-colors ${
                  isActive
                    ? "border-blue-500"
                    : "border-transparent hover:border-gray-300"
                }`}
                style={{ width: THUMB_WIDTH, height: THUMB_HEIGHT }}
              >
                {url ? (
                  <img
                    src={url}
                    alt=""
                    className="w-full h-full object-cover"
                    draggable={false}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-200">
                    <IconPhoto size={20} color="#9ca3af" />
                  </div>
                )}

                <div className="absolute top-0 left-0 px-1 py-0.5 bg-black/60 text-white text-[10px] font-semibold rounded-br-md">
                  {image.viewNumber}
                </div>

                {boxCount > 0 && (
                  <Badge
                    size="xs"
                    color="green"
                    className="absolute bottom-0.5 right-0.5"
                  >
                    {boxCount}
                  </Badge>
                )}
              </button>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
};
